import BaseService from '../../base/base_service';
import IAiListService, {
    MAX_AI_GAMES,
    IAIList,
    AI_ENGINE_TYPE,
    IAIGameRecommendations, IAIGameRecommendationsForPlayerByListType
} from './ai_list_service_interface';
import _ from '../../utils/underscore';
import {SYSTEM_EVENTS} from "../../events/events";
import {FEATURE_GAME_RECOMMENDATIONS} from "../../../../common/const/ab-testing-names";
import Store from "../../utils/store";
import {LIST_TYPE_ID} from "../../../../common/const/ai-trending-list-ids-map";
import {
    IAbTestingFeature
} from "../../../../app-react/services/ab-testing/ab-testing-feature-data-provider.service.interface";

interface AiListServiceParams {
    getTrendingRecommendedGamesForPlayerApiUrl: string,
    getGameRecommendationsForPlayerUrl:string;
    getNewGamesRecommendationsForPlayerUrl:string;
}

const AI_NEW_GAMES_KEY = 'ai_new_games';
const AI_GAMES_KEY = 'ai_games';

export default class AiListService extends BaseService implements IAiListService {
    static CLASS = 'AiListService';
    private _params: AiListServiceParams;

    private _cachedRecommendedGames: IAIGameRecommendations;
    private _cachedNewGamesRecommendations: IAIGameRecommendations;
    private _cachedRecommendedGamesForPlayerByListType: IAIGameRecommendationsForPlayerByListType = {};

    init(params) {
        super.init(params);
        this._params = params;
        this._subscribe();
    }

    private _subscribe(): void {
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
            // Related to this bug: Bug 1866277: Search results should be ordered by top 10 API
            if(this.serviceFactory.oAuth2Service().isAuthorized()) {
                this._cachedRecommendedGamesForPlayerByListType = {};
            }

            this._cachedRecommendedGames = null;
            this._cachedNewGamesRecommendations = null;
        });
    }

    clearGamesRecommendationsCache(): void {
        this._cachedRecommendedGames = null;
    }

    getAIList(maxGames: number = MAX_AI_GAMES, shouldFilterByRecentlyPlayed: boolean = true, numberOfRecentlyPlayedGameToFilterOut: number = 0): Promise<IAIList> {
        return this._resolveAIList(
            () => this.getGameRecommendations(),
            maxGames, shouldFilterByRecentlyPlayed, numberOfRecentlyPlayedGameToFilterOut,
            AI_GAMES_KEY,
            []
        );
    }

    private _getGameRecommendationsPromise: Promise<IAIGameRecommendations> = null;
    getGameRecommendations(): Promise<IAIGameRecommendations> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.reject('Can`t get game recommendations for unauthorized user');
        }

        if (!this._playerHasRecommendations()) {
            return Promise.resolve({
                engine: null,
                gameTypes: [],
                gameTypesOrderDict: {}
            });
        }

        if (this._cachedRecommendedGames) {
            return Promise.resolve(this._cachedRecommendedGames);
        }

        const engineId: string = this._getEngineID(this._getGameRecommendationsEngine()).toString();
        if (_.isEmpty(this._params.getGameRecommendationsForPlayerUrl)) {
            this.logger.debug('getGameRecommendationsForPlayerUrl is empty ');
            return Promise.resolve({
                engine: null,
                gameTypes: [],
                gameTypesOrderDict: {}
            });
        }

        if(this._getGameRecommendationsPromise) {
            return this._getGameRecommendationsPromise;
        }

        this._getGameRecommendationsPromise = this.serviceFactory.http2Service().post(this._params.getGameRecommendationsForPlayerUrl, {engineType: engineId})
            .then((data: { GameTypes: number[], Engine: string }) => {
                this._getGameRecommendationsPromise = null;
                this._cachedRecommendedGames = {
                    gameTypes: data.GameTypes,
                    engine: data.Engine,
                    gameTypesOrderDict: this._convertToGameTypesDict(data.GameTypes)
                };
                return this._cachedRecommendedGames;
            }).catch(() => {
                this._getGameRecommendationsPromise = null;
                this._cachedRecommendedGames = {
                    gameTypes:[],
                    engine: null,
                    gameTypesOrderDict:{}
                };
                return this._cachedRecommendedGames;
            });

        return this._getGameRecommendationsPromise;
    }

    getAINewGamesList(maxGames: number = MAX_AI_GAMES, shouldFilterByRecentlyPlayed: boolean = true, numberOfRecentlyPlayedGameToFilterOut: number = 0): Promise<IAIList> {
        return this._resolveAIList(
            () => this._getNewGameRecommendations(),
            maxGames, shouldFilterByRecentlyPlayed, numberOfRecentlyPlayedGameToFilterOut,
            AI_NEW_GAMES_KEY,
            {
                aiEngine: '',
                aiGameTypesArray: []
            }
        );
    }

    private _getNewGameRecommendations(): Promise<IAIGameRecommendations> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.reject('Can`t get new game recommendations for unauthorized user');
        }

        if (this._cachedNewGamesRecommendations) {
            return Promise.resolve(this._cachedNewGamesRecommendations);
        }

        return this.serviceFactory.http2Service().post(this._params.getNewGamesRecommendationsForPlayerUrl)
            .then((data: { GameTypes: number[], Engine: string }) => {
                this._cachedNewGamesRecommendations = {
                    gameTypes: data.GameTypes,
                    engine: AI_ENGINE_TYPE.ML888,
                    gameTypesOrderDict: this._convertToGameTypesDict(data.GameTypes)
                };

                return this._cachedNewGamesRecommendations;
            });
    }

    async getTrendingRecommendedGamesForPlayer(listTypeIds: LIST_TYPE_ID[]): Promise<IAIGameRecommendationsForPlayerByListType> {
        const countryCode = this.serviceFactory.countryService().getCountry();

        if (_.isNullOrUndefined(countryCode) || _.isEmpty(this._params?.getTrendingRecommendedGamesForPlayerApiUrl)) {
            return {};
        }

        // If we already have in runtime memory the response for all the requested listTypeIDs
        if(!_.isEmpty(this._cachedRecommendedGamesForPlayerByListType) && listTypeIds.every(listID => this._cachedRecommendedGamesForPlayerByListType.hasOwnProperty(listID))){
            return this._cachedRecommendedGamesForPlayerByListType;
        }

        const gameTypesPerListID: IAIGameRecommendationsForPlayerByListType = await this.serviceFactory.http2Service()
            .post(this._params.getTrendingRecommendedGamesForPlayerApiUrl,
                {
                    listTypeIds: listTypeIds,
                    subBrandId: this.serviceFactory.skinService().getSubBrandID(),
                    countryCode
                });

        for (let listID in gameTypesPerListID){
            if(gameTypesPerListID.hasOwnProperty(listID)) {
                this._cachedRecommendedGamesForPlayerByListType[listID] = await this._filterGameTypes(gameTypesPerListID[listID], false, 0);
            }
        }

        return this._cachedRecommendedGamesForPlayerByListType;
    }

    sortGames(gameTypes: number[]): Promise<number[]> {
        return this.getGameRecommendations().then(
            (gameRecommendations) => AiListService._sortGames(gameTypes, gameRecommendations.gameTypesOrderDict)
        );
    }

    private static _sortGames(gameTypes: number[], gameTypesDict: { [gameType: number]: number }): number[] {
        if (!_.isObject(gameTypesDict)) {
            return gameTypes;
        }

        return gameTypes.sort((gt1, gt2) => {
            const index1 = gameTypesDict[gt1];
            const index2 = gameTypesDict[gt2];

            if (_.isUndefined(index1)) { //if gt1 is not in the AI recommendations
                if (_.isUndefined(index2)) { //and if gt2 also is not in the AI recommendations
                    return -1; // then gt1 goes before gt2 (keeping the same order)
                } else {
                    return 1; // otherwise gt1 goes after gt2 (any game that is in the AI recommendation goes before those that are not in the AI recommendations)
                }
            }

            if (_.isUndefined(index2)) { //if gt2 is not in the AI recommendations obviously gt1 is in the AI recommended games (otherwise the previous if statement will apply)
                return -1; // so gt2 goes after gt1
            }

            return (index1 < index2) ? -1 : 1; //gt1 goes before gt2 if index1 < index2
        });
    }

    private _resolveAIList(
        apiFunction: () => Promise<IAIGameRecommendations>,
        maxGames: number, shouldFilterByRecentlyPlayed: boolean, numberOfRecentlyPlayedGameToFilterOut: number, storeKey: string, defaultValueForKey: any) {

        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            const aiGamesStored = Store.get(storeKey, JSON.stringify(defaultValueForKey));

            return Promise.resolve(JSON.parse(aiGamesStored));
        }

        return apiFunction()
            .then(data => {
                data.gameTypes = data.gameTypes || [];
                return this._filterRecommendedGames(data, maxGames, shouldFilterByRecentlyPlayed, numberOfRecentlyPlayedGameToFilterOut).then((aiGames)=> {
                    Store.set(storeKey, JSON.stringify(aiGames));
                    return aiGames;
                });
            }).catch((err)=> {
                this.logger.error(`_resolveAIList api call failed for store key:${storeKey}. Error: ${JSON.stringify(err)}`);
                Store.del(storeKey);
                return defaultValueForKey;
            });
    }

    private _filterRecommendedGames(aiRecommendations, maxGames, shouldFilterByRecentlyPlayed, numberOfRecentlyPlayedGameToFilterOut) {
        return this._filterGameTypes(aiRecommendations.gameTypes, shouldFilterByRecentlyPlayed, numberOfRecentlyPlayedGameToFilterOut)
            .then(gameTypes => gameTypes.slice(0, maxGames))
            .then(gameTypes => {
                return {
                    aiEngine: aiRecommendations.engine,
                    aiGameTypesArray: gameTypes
                };
            });
    }

    private _filterGameTypes(gameTypes, shouldFilterByRecentlyPlayed, numberOfRecentlyPlayedGameToFilterOut) {
        let result;

        if (shouldFilterByRecentlyPlayed) {
            result = this._filterByRecentlyPlayed(gameTypes, numberOfRecentlyPlayedGameToFilterOut);
        } else {
            result = Promise.resolve(gameTypes);
        }

        return result
            .then(filteredGameTypes => this._filterByLayout(filteredGameTypes))
            .then(filteredGameTypes => this._filterAllowedToPlayRealGames(filteredGameTypes));
    }

    /**
     * @description Get the recently played game type array and remove them for the AI games list
     * @numberOfRecentlyPlayedGameToFilterOut By default we will filter out all the recently played games,
     * unless we got a specific number of games to filter out
     * @Note Games that are in the recently played list will not be shown to the user in AI list
     * @return {boolean}
     */
    private _filterByRecentlyPlayed(gameTypes, numberOfRecentlyPlayedGameToFilterOut) {
        return this.serviceFactory.recentlyPlayedService().getRecentlyPlayedList().then(recentlyPlayedGameTypes => {
            recentlyPlayedGameTypes = recentlyPlayedGameTypes || [];
            if (numberOfRecentlyPlayedGameToFilterOut) {
                recentlyPlayedGameTypes = recentlyPlayedGameTypes.slice(0, numberOfRecentlyPlayedGameToFilterOut);
            }
            return gameTypes.filter(gameType => recentlyPlayedGameTypes.indexOf(gameType) === -1);
        });
    }

    private _filterByLayout(gameTypes) {
        const result = this.serviceFactory.integrationService().filterInLayoutGames(gameTypes);
        if (result) {
            return result;
        }

        return Promise.resolve(gameTypes);
    }

    async _filterAllowedToPlayRealGames(gameTypes) {
        const result = await this.serviceFactory.integrationService().filterAllowedToPlayRealGames(gameTypes);
        if (result) {
            return result;
        }

        return Promise.resolve(gameTypes);
    }

    private _playerHasRecommendations(): boolean {
        const engine = this._getGameRecommendationsEngine();
        if (engine) {
            const noRecommendationMatches = engine.match(/no\s*recommendation/gi) || [];
            return noRecommendationMatches.length === 0;
        }
        return true;
    }

    private _getGameRecommendationsEngine(): string {
        let engine: string;
        const gameRecommendations: IAbTestingFeature = this.serviceFactory.abTestingService().getAbTestingDataForFeature(FEATURE_GAME_RECOMMENDATIONS);
        if (gameRecommendations) {
            engine = (gameRecommendations.Properties || {}).Engine;
        }
        return engine;
    }

    private _getEngineID(engine: string): number {
        engine = engine ? engine.toUpperCase() : engine;
        if (engine === AI_ENGINE_TYPE.ML888) {
            return 1;
        } else {
            return 0;
        }
    }

    private _convertToGameTypesDict(gameTypes: number[]): { [key: number]: number } {
        const dict: { [key: number]: number } = {};
        if (!gameTypes) {
            return dict;
        }

        gameTypes.forEach((gameType, index) => {
            dict[gameType] = index;
        });

        return dict;
    }
}
